import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Home, LogOut, Menu, Search, type LucideIcon } from "lucide-react";
import type {
  OverviewResponse,
  PlansOverviewResponse,
  RegistrationsResponse,
  Role,
  VolunteerApplicationsResponse,
} from "@mubin/contracts";
import { api } from "../lib/api";
import { PORTAL_ROLES, roleLabel } from "../lib/labels";
import { useCan } from "../lib/org";
import { SECTIONS, type Section } from "../lib/sections";
import { logout, useMe, useRoleSwitch } from "../lib/session";

export function Shell() {
  const { data: me } = useMe();
  const switchRole = useRoleSwitch();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const portalRoles = (me?.roles ?? []).filter((r) => PORTAL_ROLES.includes(r));
  const activeIsPortal = !!me?.activeRole && PORTAL_ROLES.includes(me.activeRole);
  const fallbackRole = portalRoles[0];

  // The server defaults to the highest role; if that is not a portal role, move to one that is.
  useEffect(() => {
    if (me && !activeIsPortal && fallbackRole) void switchRole(fallbackRole);
  }, [me, activeIsPortal, fallbackRole, switchRole]);

  if (!me) return null;

  return (
    <div className="shell">
      <aside className="side">
        <div className="logo">
          <img src="/logo-dark.png" alt="مُبين" />
        </div>
        <SideNav />
        <div className="side-org">
          <img src="/org/horizontal-white.png" alt="الجمعية الخيرية لتحفيظ القرآن الكريم بمحافظة رماح" />
        </div>
      </aside>
      <div className="main">
        <header className="topbar">
          <div className="who">
            <div className="avatar" aria-hidden="true">
              {initial(me.user.name)}
            </div>
            <div>
              <div className="name">{me.user.name}</div>
              <div className="org">{me.organizations.map((o) => o.name).join("، ") || "—"}</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {portalRoles.length > 1 && (
              <div className="roles" role="group" aria-label="الدور الحالي">
                {portalRoles.map((r) => (
                  <button key={r} aria-pressed={me.activeRole === r} onClick={() => void switchRole(r)}>
                    {roleLabel[r]}
                  </button>
                ))}
              </div>
            )}
            <button
              className="btn btn-ghost"
              onClick={async () => {
                await logout(qc);
                await navigate({ to: "/login" });
              }}
            >
              <LogOut size={18} strokeWidth={1.75} />
              خروج
            </button>
          </div>
        </header>
        {portalRoles.length === 0 ? <MobileOnly roles={me.roles} /> : <Outlet />}
      </div>
    </div>
  );
}

type NavItem = { to: string; label: string; icon: ReactNode; show: boolean; badge?: number };
type NavGroup = { id: string; label: string; items: NavItem[] };
const OPEN_KEY = "mubin.navOpen";
const icon = (I: LucideIcon) => <I size={18} strokeWidth={1.75} />;

/**
 * The side menu (owner's choice, 2026-11-01): the pages in sections that fold, with the section of the
 * current page open and the others as the user left them, a quick search, and a count on what waits for
 * action. Only pages the active role may open are listed (the server authorizes every request again).
 */
function SideNav() {
  const { data: me } = useMe();
  const can = useCan();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const role = me?.activeRole;
  const slow = { refetchInterval: 60_000, staleTime: 30_000 } as const;
  const overview = useQuery({
    queryKey: ["overview", role],
    queryFn: () => api<OverviewResponse>("/v1/admin/overview"),
    enabled: can.has("join.manage") || can.has("deletion.manage"),
    ...slow,
  });
  const plans = useQuery({
    queryKey: ["plans-overview", role],
    queryFn: () => api<PlansOverviewResponse>("/v1/plans/overview"),
    enabled: can.has("plans.view"),
    ...slow,
  });
  const volunteers = useQuery({
    queryKey: ["volunteers", role, "new"],
    queryFn: () => api<VolunteerApplicationsResponse>("/v1/volunteer-applications?status=new"),
    enabled: can.has("volunteers.manage"),
    ...slow,
  });
  const registrations = useQuery({
    queryKey: ["registrations", role, "pending"],
    queryFn: () => api<RegistrationsResponse>("/v1/student-registrations?status=pending"),
    enabled: can.has("join.manage"),
    ...slow,
  });

  // What waits for action, by page.
  const badges: Record<string, number | undefined> = {
    "/plans": plans.data?.rows.filter((r) => r.draftId).length,
    "/new-students": registrations.data?.registrations.length,
    "/join-requests": overview.data?.pendingJoinRequests,
    "/volunteers": volunteers.data?.applications.length,
    "/deletion-requests": overview.data?.pendingDeletionRequests,
  };
  const groups: NavGroup[] = SECTIONS.map((s: Section) => ({
    id: s.id,
    label: s.label,
    items: s.pages.map((p) => ({
      to: p.to,
      label: p.label,
      icon: icon(p.icon),
      show: p.opensWith === "fullAdmin" ? can.fullAdmin : can.has(p.opensWith),
      badge: badges[p.to],
    })),
  }))
    .map((g) => ({ ...g, items: g.items.filter((i) => i.show) }))
    .filter((g) => g.items.length > 0);

  const isHere = (to: string) => path === to || path.startsWith(`${to}/`);
  const current = groups.find((g) => g.items.some((i) => isHere(i.to)))?.id;
  const [open, setOpen] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(OPEN_KEY) ?? "[]") as string[]);
    } catch {
      return new Set();
    }
  });
  const toggle = (id: string, isOpen: boolean) =>
    setOpen((o) => {
      const n = new Set(o);
      if (isOpen) n.delete(id);
      else n.add(id);
      try {
        localStorage.setItem(OPEN_KEY, JSON.stringify([...n]));
      } catch {
        /* storage unavailable: the sections still fold for this visit */
      }
      return n;
    });

  // The quick search: Ctrl+K (or ⌘K) focuses it; Enter opens the first match.
  const [q, setQ] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const norm = (t: string) => t.replace(/[أإآ]/g, "ا").replace(/ة/g, "ه").replace(/ى/g, "ي").trim();
  const matches = q.trim()
    ? groups.flatMap((g) => g.items).filter((i) => norm(i.label).includes(norm(q)))
    : [];

  // On narrow screens the menu folds behind a button; it closes after a page is chosen.
  const [shown, setShown] = useState(false);
  useEffect(() => setShown(false), [path]);

  const Item = ({ i }: { i: NavItem }) => (
    <Link to={i.to}>
      {i.icon}
      <span className="nav-label">{i.label}</span>
      {!!i.badge && <span className="nav-badge">{i.badge > 99 ? "99+" : i.badge}</span>}
    </Link>
  );

  return (
    <>
      <button
        type="button"
        className="nav-toggle"
        aria-expanded={shown}
        aria-controls="side-nav"
        onClick={() => setShown((v) => !v)}
      >
        <Menu size={20} /> القائمة
      </button>
      <nav id="side-nav" className={`nav${shown ? " shown" : ""}`} aria-label="التنقل الرئيسي">
        <label className="nav-search">
          <Search size={16} aria-hidden="true" />
          <input
            ref={searchRef}
            type="search"
            placeholder="ابحث عن صفحة…"
            aria-label="ابحث عن صفحة"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && matches[0]) {
                setQ("");
                void navigate({ to: matches[0].to });
              } else if (e.key === "Escape") setQ("");
            }}
          />
          <kbd>Ctrl K</kbd>
        </label>
        {q.trim() ? (
          matches.length ? (
            matches.map((i) => (
              <span key={i.to} onClick={() => setQ("")}>
                <Item i={i} />
              </span>
            ))
          ) : (
            <p className="nav-empty">لا توجد صفحة بهذا الاسم</p>
          )
        ) : (
          <>
            <Link to="/" activeOptions={{ exact: true }}>
              {icon(Home)}
              <span className="nav-label">الرئيسية</span>
            </Link>
            {groups.map((g) => {
              const isOpen = g.id === current || open.has(g.id);
              const waiting = g.items.reduce((n, i) => n + (i.badge ?? 0), 0);
              return (
                <div key={g.id} className="nav-group">
                  <button
                    type="button"
                    className="nav-group-head"
                    aria-expanded={isOpen}
                    aria-controls={`nav-${g.id}`}
                    onClick={() => g.id !== current && toggle(g.id, isOpen)}
                  >
                    <span>{g.label}</span>
                    {!isOpen && waiting > 0 && (
                      <span className="nav-badge">{waiting > 99 ? "99+" : waiting}</span>
                    )}
                    <ChevronDown size={16} className="nav-chev" aria-hidden="true" />
                  </button>
                  {isOpen && (
                    <div className="nav-group-items" id={`nav-${g.id}`}>
                      {g.items.map((i) => (
                        <Item key={i.to} i={i} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </nav>
    </>
  );
}

/** First letter of the name, skipping honorifics such as «الشيخ». */
function initial(name: string): string {
  const words = name.trim().split(/\s+/);
  const honorifics = new Set(["الشيخ", "الأستاذ", "الأستاذة", "د.", "الدكتور"]);
  const word = words.find((w) => !honorifics.has(w)) ?? words[0] ?? "";
  return word.charAt(0);
}

function MobileOnly({ roles }: { roles: Role[] }) {
  return (
    <div className="card">
      <div className="empty">
        <b>هذه البوابة للإشراف والإدارة</b>
        <span>
          حسابك ({roles.map((r) => roleLabel[r]).join("، ") || "بلا دور"}) يعمل من تطبيق مُبين على الجوال. إن
          كنت تحتاج صلاحية إشراف، اطلبها من إدارة المجمع.
        </span>
      </div>
    </div>
  );
}
